import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEthPrice } from '~/core/hooks/useEthPrice';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

type UseBlinkoEthPriceOptions = {
  isGameActive: boolean;
  refreshIntervalMs?: number;
};

export const useBlinkoEthPrice = ({
  isGameActive,
  refreshIntervalMs = FIVE_MINUTES_MS,
}: UseBlinkoEthPriceOptions) => {
  const { data: latestPrice, isLoading, error, mutate } = useEthPrice();
  const [sessionPrice, setSessionPrice] = useState<number | null>(null);
  const prevActiveRef = useRef<boolean>(isGameActive);

  useEffect(() => {
    const prevActive = prevActiveRef.current;
    prevActiveRef.current = isGameActive;

    if (isGameActive) {
      setSessionPrice(prev => {
        if (prev !== null && prev > 0) {
          return prev;
        }
        const resolved = latestPrice ?? prev ?? 0;
        return resolved > 0 ? resolved : prev;
      });
    } else {
      if (prevActive) {
        mutate();
      }
      setSessionPrice(null);
    }
  }, [isGameActive, latestPrice, mutate]);

  useEffect(() => {
    if (!isGameActive) {
      return;
    }
    if ((sessionPrice === null || sessionPrice <= 0) && latestPrice && latestPrice > 0) {
      setSessionPrice(latestPrice);
    }
  }, [isGameActive, latestPrice, sessionPrice]);

  useEffect(() => {
    if (isGameActive) {
      return;
    }

    const intervalId = window.setInterval(() => {
      mutate();
    }, refreshIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isGameActive, mutate, refreshIntervalMs]);

  const priceUsd = useMemo(() => {
    if (sessionPrice !== null && sessionPrice > 0) {
      return sessionPrice;
    }
    if (latestPrice && latestPrice > 0) {
      return latestPrice;
    }
    return 0;
  }, [latestPrice, sessionPrice]);

  const priceReady = priceUsd > 0;
  const loading = !priceReady && (isLoading || isGameActive);

  const refreshNow = useCallback(() => mutate(), [mutate]);

  return {
    priceUsd,
    isLoading: loading,
    error,
    refresh: refreshNow,
    isSessionLocked: sessionPrice !== null && sessionPrice > 0,
  };
};
